import React, { useMemo, useRef } from 'react'
import ReactEChartsCore from './EChartsCore.js'
import * as echarts from 'echarts/core'
import type { EChartsType } from 'echarts/core'
import { BarChart, CustomChart, LineChart } from 'echarts/charts'
import { GridComponent, LegendComponent, TooltipComponent } from 'echarts/components'
import { CanvasRenderer } from 'echarts/renderers'
import { stackedBarOption } from './Options/stackedBarOption.js'
import { CHART_SURFACE } from './Options/visualIdentity.js'
import type { StackSeries } from './StackSeries.ts'

echarts.use([BarChart, CustomChart, LineChart, GridComponent, LegendComponent, TooltipComponent, CanvasRenderer])

interface Props {
  data: unknown[]
  stackSeries: StackSeries[]
  onBarClick?: (label: string) => void
  ylabel: string
  xlabel: string
  dataKey: string // e.g. "xAxisvalue"
  xTickMaxChars?: number
  xTickRotate?: number
  height?: number | `${number}%`
  isPercent?: boolean
  /** Series style. `'bar'` (default) keeps the original stacked-bar behaviour; `'line'`/`'area'`
   *  render the same stacked data as a line / filled-area chart (shared axes, tooltip, click). */
  variant?: 'bar' | 'line' | 'area'
  /** Samples per bucket, aligned to `data` by index; shown in the tooltip. */
  sampleCounts?: (number | undefined)[]
}

/**
 * The echarts `option` (series + axes + tooltip) is built by the PURE `stackedBarOption` builder
 * (Options/stackedBarOption.ts) so this UI and a future server-side render sidecar share one source
 * of truth. The hovered-series tooltip highlight state stays here (a React ref) and is handed to the
 * builder as a plain `getHoveredSeries` accessor.
 */
export const StackedBarChartNext: React.FC<Props> = ({
  data,
  stackSeries,
  dataKey,
  onBarClick,
  ylabel,
  xlabel,
  xTickRotate = 90,
  xTickMaxChars = 10,
  height,
  isPercent = false,
  variant = 'bar',
  sampleCounts
}) => {
  // Track the currently hovered bar segment's series name (short label)
  const hoveredSeriesRef = useRef<string | null>(null)

  const option = useMemo(
    () =>
      stackedBarOption({
        data,
        stackSeries,
        dataKey,
        ylabel,
        xlabel,
        xTickRotate,
        xTickMaxChars,
        isPercent,
        variant,
        sampleCounts,
        getHoveredSeries: () => hoveredSeriesRef.current
      }),
    [data, stackSeries, dataKey, ylabel, xlabel, xTickRotate, xTickMaxChars, isPercent, variant, sampleCounts]
  )

  function typecheckEvent(
    p: unknown
  ): p is { componentType: string | null; seriesName: string | null; seriesIndex: number | null; seriesId?: string | null } {
    return (
      p !== null &&
      p !== undefined &&
      typeof p === 'object' &&
      'seriesName' in p &&
      (p.seriesName === null || typeof p.seriesName === 'string') &&
      'seriesIndex' in p &&
      (p.seriesIndex === null || typeof p.seriesIndex === 'number') &&
      'componentType' in p &&
      (p.componentType === null || typeof p.componentType === 'string')
    )
  }

  const onEvents = useMemo(() => {
    return {
      mouseover: (p) => {
        // When hovering a bar segment, set hovered series name
        if (typecheckEvent(p)) {
          hoveredSeriesRef.current = typeof p.seriesName === 'string' ? p.seriesName : null
        }
      },
      mouseout: (p) => {
        if (typecheckEvent(p) && p.componentType === 'series') {
          hoveredSeriesRef.current = null
        }
      },
      click: onBarClick
        ? (p, chart) => {
            if (typecheckEvent(p)) {
              // Positional series ids make echarts reuse these bars at the next level, hover blur
              // included, so every other series would open faded until the pointer moves.
              chart?.dispatchAction({ type: 'downplay' })
              hoveredSeriesRef.current = null
              // seriesId is stable; seriesIndex can be wrong if ECharts merges/reorders.
              const byId = typeof p.seriesId === 'string' ? stackSeries.find((s) => s.id === p.seriesId) : undefined
              const idx = typeof p.seriesIndex === 'number' ? p.seriesIndex : -1
              const hit = byId ?? (idx >= 0 ? stackSeries[idx] : undefined)
              onBarClick(hit?.label ?? p.seriesName ?? '')
            }
          }
        : undefined
    } as Record<string, (params: unknown, chart?: Pick<EChartsType, 'dispatchAction'>) => void>
  }, [stackSeries, onBarClick])

  return (
    <ReactEChartsCore
      echarts={echarts}
      key={onBarClick ? 'clickable' : 'noclick'}
      option={option}
      onEvents={onEvents}
      notMerge={true}
      lazyUpdate={true}
      style={{ width: '100%', height: height ?? 500, backgroundColor: CHART_SURFACE }}
    />
  )
}
